import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { BotQuestionDTO } from '@sac/contracts'
import { api } from '../lib/api'
import { Icon } from '../components/icons'
import { useToast } from '../components/ui/Toaster'

/**
 * Robôs — perguntas de múltipla escolha vindas de um robô, respondidas com um clique.
 *
 * A regra desta tela, e o motivo de ela ser tão pequena: **não há campo de texto**. O robô já tem
 * o contexto (qual pedido, qual loja, o que ele quer fazer) e formula a pergunta fechada; quem
 * está no SAC só decide. Um campo livre aqui devolveria ao robô algo que ele não sabe interpretar
 * e transformaria uma decisão em mais uma caixa de entrada para ler.
 *
 * O clique é irreversível — a pergunta típica é "posso abrir uma disputa na Shopee Kids?" e o
 * robô age assim que recebe. Daí as três únicas defesas que cabem dentro de "clicou, enviou":
 * o botão desabilita no clique, as opções ficam espaçadas, e o cartão mostra o que foi escolhido
 * antes de sair da lista.
 */

function Cartao({ q, onPronto }: { q: BotQuestionDTO; onPronto: () => void }) {
  const [escolhido, setEscolhido] = useState<string | null>(null)
  const qc = useQueryClient()
  const toast = useToast()

  const responder = useMutation({
    mutationFn: (optionId: string) => api.answerBotQuestion(q.id, optionId),
    onMutate: (optionId) => setEscolhido(optionId),
    onSuccess: () => {
      // O recibo fica visível um instante: quem autorizou uma disputa merece ver o que autorizou.
      setTimeout(onPronto, 1200)
    },
    onError: (err: Error & { status?: number; detail?: string | null }) => {
      setEscolhido(null)
      if (err.status === 409) {
        toast(err.detail ?? 'Alguém já respondeu esta pergunta.')
        void qc.invalidateQueries({ queryKey: ['bot-questions'] })
        return
      }
      toast(err.detail ?? 'Não consegui registrar a resposta.')
    },
  })

  const enviado = responder.isSuccess
  const contexto = Object.entries(q.context ?? {}).filter(([, v]) => typeof v === 'string' && v)

  return (
    <div className={`robo-card ${enviado ? 'ok' : ''}`}>
      <div className="robo-de">
        <Icon name="robot" size={13} />
        {q.bot}
        <span className="robo-quando">{new Date(q.createdAt).toLocaleString('pt-BR')}</span>
      </div>

      <p className="robo-q">{q.question}</p>

      {contexto.length > 0 && (
        <div className="robo-ctx">
          {contexto.map(([k, v]) => (
            <span key={k}>
              {k}: <code>{String(v)}</code>
            </span>
          ))}
        </div>
      )}

      {enviado ? (
        <div className="robo-recibo">
          <Icon name="ck" size={14} />
          Respondido: <strong>{q.options.find((o) => o.id === escolhido)?.label}</strong>
        </div>
      ) : (
        <div className="robo-opts">
          {q.options.map((o) => (
            <button
              key={o.id}
              type="button"
              className={`robo-opt ${escolhido === o.id ? 'escolhida' : ''}`}
              // Um duplo-clique não pode virar duas respostas: o POST é irreversível.
              disabled={responder.isPending}
              onClick={() => responder.mutate(o.id)}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function Robos() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['bot-questions'],
    queryFn: () => api.botQuestions('pending'),
    // Ninguém fica olhando a aba. 15s é curto o bastante para não parecer travado e longo o
    // bastante para não valer estender o SSE, que hoje só liga na seção de conversas.
    refetchInterval: 15_000,
  })

  const recarregar = () => void qc.invalidateQueries({ queryKey: ['bot-questions'] })

  return (
    <div className="robos">
      <div className="robos-head">
        <h2>Perguntas dos robôs</h2>
        <p>
          Um robô pergunta, você escolhe uma opção. A escolha volta para ele, que executa — o SAC
          registra a decisão e não age sozinho.
        </p>
      </div>

      {isLoading && <div className="robos-vazio">Carregando…</div>}

      {!isLoading && (data?.length ?? 0) === 0 && (
        <div className="robos-vazio">
          <Icon name="ck" size={20} />
          Nenhuma pergunta esperando.
        </div>
      )}

      {data?.map((q) => (
        <Cartao key={q.id} q={q} onPronto={recarregar} />
      ))}
    </div>
  )
}
